import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackAddToCart } from "@/utils/metaPixel";

export interface CartItem {
  id: string;
  name: string;
  price: string;
  priceNum: number;
  image: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const loadedRef = useRef(false);
  const syncingRef = useRef(false);
  const navigate = useNavigate();

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      if (!uid) {
        loadedRef.current = false;
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load cart from DB when user logs in
  useEffect(() => {
    if (!userId || loadedRef.current) return;
    loadedRef.current = true;

    const loadCart = async () => {
      const { data } = await supabase
        .from("cart_items")
        .select("*")
        .eq("user_id", userId);

      if (data && data.length > 0) {
        setItems(data.map((row: any) => ({
          id: row.product_id,
          name: row.product_name,
          price: row.product_price,
          priceNum: Number(row.product_price_num),
          image: row.product_image,
          quantity: row.quantity,
        })));
      }
    };

    loadCart();
  }, [userId]);

  // Sync cart to DB whenever items change (debounced)
  useEffect(() => {
    if (!userId || !loadedRef.current || syncingRef.current) return;

    const timeout = setTimeout(async () => {
      syncingRef.current = true;

      // Delete all and re-insert
      await supabase.from("cart_items").delete().eq("user_id", userId);

      if (items.length > 0) {
        const rows = items.map((item) => ({
          user_id: userId,
          product_id: item.id,
          product_name: item.name,
          product_price: item.price,
          product_price_num: item.priceNum,
          product_image: item.image,
          quantity: item.quantity,
        }));
        await supabase.from("cart_items").insert(rows);
      }

      syncingRef.current = false;
    }, 500);

    return () => clearTimeout(timeout);
  }, [items, userId]);

  const addItem = useCallback((item: Omit<CartItem, "quantity">) => {
    if (!userId) {
      navigate("/auth");
      return;
    }
    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    trackAddToCart(item.id, item.name, item.priceNum);
    setIsOpen(true);
  }, [userId, navigate]);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, quantity } : i));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.priceNum * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, isOpen, setIsOpen, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
